<template>
  <div class="admin-main">

    <div class="header">
      <h2>회원 관리</h2>
    </div>

    <div class="content">

      <!-- 검색 / 필터 -->
      <div class="toolbar">
        <div class="search-box">
          <i class="ph ph-magnifying-glass"></i>
          <input 
            type="text" 
            v-model="searchText" 
            placeholder="이름, 아이디, 닉네임, 이메일로 검색..."
            @input="onSearch"
          />
          <span class="clear-btn" v-if="searchText" @click="clearSearch">
            <i class="ph ph-x"></i>
          </span>
        </div>
        <div class="filter-tabs">
          <button 
            v-for="tab in levelTabs" :key="tab.value"
            :class="['tab', { active: currentTab === tab.value }]"
            @click="changeTab(tab.value)"
          >
            {{ tab.label }}
            <em v-if="tab.count > 0">{{ tab.count }}</em>
          </button>
        </div>
      </div>

      <!-- 회원 테이블 -->
      <div class="table-wrap" v-if="filteredMembers.length > 0">
        <table>
          <thead>
            <tr>
              <th class="col-id">아이디</th>
              <th class="col-name">이름</th>
              <th class="col-nick">닉네임</th>
              <th class="col-email">이메일</th>
              <th class="col-level">등급</th>
              <th class="col-date">가입일</th>
              <th class="col-login">최근 로그인</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="m in pagedMembers" :key="m.idx"
              :class="{ selected: selectedMember?.idx === m.idx }"
              @click="selectMember(m)"
            >
              <td class="col-id">{{ m.mb_id }}</td>
              <td class="col-name">
                <div class="name-cell">
                  <div class="avatar" v-if="m.image">
                    <img :src="m.image" />
                  </div>
                  <div class="avatar placeholder" v-else>
                    <i class="ph ph-user"></i>
                  </div>
                  <span>{{ m.name || '-' }}</span>
                </div>
              </td>
              <td class="col-nick">{{ m.nickname || '-' }}</td>
              <td class="col-email">{{ m.email || '-' }}</td>
              <td class="col-level">
                <span class="level-badge" :class="'lv' + m.level">{{ levelLabel(m.level) }}</span>
              </td>
              <td class="col-date">{{ formatDate(m.created_at) }}</td>
              <td class="col-login">{{ m.last_login ? formatDate(m.last_login) : '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="empty" v-else>
        <i class="ph ph-users-three"></i>
        <p>{{ searchText ? '검색 결과가 없습니다.' : '등록된 회원이 없습니다.' }}</p>
      </div>

      <!-- 페이지네이션 -->
      <div class="pagination" v-if="totalPages > 1">
        <button :disabled="currentPage === 1" @click="currentPage--">
          <i class="ph ph-caret-left"></i>
        </button>
        <template v-for="p in visiblePages" :key="p">
          <button 
            v-if="p !== '...'"
            :class="{ active: currentPage === p }" 
            @click="currentPage = p"
          >{{ p }}</button>
          <span v-else class="dots">...</span>
        </template>
        <button :disabled="currentPage === totalPages" @click="currentPage++">
          <i class="ph ph-caret-right"></i>
        </button>
      </div>

    </div>

    <!-- 상세 패널 -->
    <transition name="slide">
      <div class="detail-panel" v-if="selectedMember">
        <div class="panel-header">
          <h2>회원 상세</h2>
          <span @click="selectedMember = null"><i class="ph ph-x"></i></span>
        </div>

        <div class="panel-body">

          <!-- 프로필 영역 -->
          <div class="profile-section">
            <div class="profile-avatar" v-if="selectedMember.image">
              <img :src="selectedMember.image" />
            </div>
            <div class="profile-avatar placeholder" v-else>
              <i class="ph ph-user"></i>
            </div>
            <h3>{{ selectedMember.name || selectedMember.mb_id }}</h3>
            <span class="level-badge lg" :class="'lv' + selectedMember.level">
              {{ levelLabel(selectedMember.level) }}
            </span>
          </div>

          <!-- 등급 변경 -->
          <div class="detail-field">
            <label>등급 변경</label>
            <div class="level-actions">
              <button 
                v-for="lv in levelOptions" :key="lv.value"
                :class="['level-btn', 'lv' + lv.value, { active: editForm.level === lv.value }]"
                @click="editForm.level = lv.value"
              >
                {{ lv.label }}
              </button>
            </div>
          </div>

          <div class="section-divider"><span>기본 정보</span></div>

          <div class="detail-field">
            <label>아이디 (수정 불가)</label>
            <span class="readonly">{{ selectedMember.mb_id }}</span>
          </div>

          <div class="detail-field">
            <label>이메일</label>
            <input type="text" v-model="editForm.email" placeholder="이메일" />
          </div>

          <div class="detail-field">
            <label>비밀번호 변경</label>
            <input type="password" v-model="editForm.password" placeholder="변경 시에만 입력" />
          </div>

          <div class="detail-field">
            <label>이름</label>
            <input type="text" v-model="editForm.name" placeholder="이름" />
          </div>

          <div class="detail-field">
            <label>성</label>
            <input type="text" v-model="editForm.first_name" placeholder="성" />
          </div>

          <div class="detail-field">
            <label>풀네임</label>
            <input type="text" v-model="editForm.full_name" placeholder="풀네임" />
          </div>

          <div class="detail-field">
            <label>닉네임</label>
            <input type="text" v-model="editForm.nickname" placeholder="닉네임" />
          </div>

          <div class="detail-field">
            <label>연락처</label>
            <input type="text" v-model="editForm.phone" placeholder="연락처" />
          </div>

          <div class="detail-field">
            <label>주민등록번호</label>
            <input type="text" v-model="editForm.citizen_number" placeholder="주민등록번호" />
          </div>

          <div class="section-divider"><span>추가 정보</span></div>

          <div class="detail-field">
            <label>주소</label>
            <input type="text" v-model="editForm.address" placeholder="주소" />
          </div>

          <div class="detail-field">
            <label>글로벌 주소</label>
            <input type="text" v-model="editForm.global_address" placeholder="글로벌 주소" />
          </div>

          <div class="detail-field">
            <label>소개</label>
            <input type="text" v-model="editForm.introduce" placeholder="소개" />
          </div>

          <div class="detail-field">
            <label>프로필 이미지 URL</label>
            <input type="text" v-model="editForm.image" placeholder="이미지 URL" />
          </div>

          <div class="detail-field">
            <label>그룹</label>
            <input type="text" v-model="editForm.group" placeholder="그룹" />
          </div>

          <div class="detail-field">
            <label>클래스</label>
            <input type="text" v-model="editForm.class" placeholder="클래스" />
          </div>

          <div class="detail-field">
            <label>자격증</label>
            <textarea v-model="editForm.license" placeholder="자격증" rows="2"></textarea>
          </div>

          <div class="detail-field">
            <label>권한 (permission)</label>
            <textarea v-model="editForm.permission" placeholder="권한 JSON" rows="2"></textarea>
          </div>

          <div class="section-divider"><span>포인트 / 인증</span></div>

          <div class="detail-field row">
            <div class="half">
              <label>캐시</label>
              <input type="number" v-model.number="editForm.cash" />
            </div>
            <div class="half">
              <label>포인트</label>
              <input type="number" v-model.number="editForm.point" />
            </div>
          </div>

          <div class="detail-field">
            <label>본인인증 여부</label>
            <div class="level-actions">
              <button 
                :class="['level-btn', { active: editForm.self_certify === 1 }]"
                @click="editForm.self_certify = 1"
              >인증됨</button>
              <button 
                :class="['level-btn', { active: editForm.self_certify === 0 }]"
                @click="editForm.self_certify = 0"
              >미인증</button>
            </div>
          </div>

          <div class="section-divider"><span>시스템 정보 (읽기 전용)</span></div>

          <div class="detail-field">
            <label>가입일</label>
            <span class="readonly">{{ formatDateTime(selectedMember.created_at) }}</span>
          </div>

          <div class="detail-field">
            <label>최근 로그인</label>
            <span class="readonly">{{ selectedMember.last_login ? formatDateTime(selectedMember.last_login) : '-' }}</span>
          </div>

          <div class="detail-field">
            <label>최근 IP</label>
            <span class="readonly">{{ selectedMember.last_ip || '-' }}</span>
          </div>

          <div class="detail-field">
            <label>최근 위치</label>
            <span class="readonly">{{ selectedMember.last_location || '-' }}</span>
          </div>

        </div>

        <div class="panel-footer">
          <button class="btn-delete" @click="deleteMember(selectedMember)">
            <i class="ph ph-trash"></i> 삭제
          </button>
          <button class="btn-save" @click="saveMember" :disabled="isSaving">
            <i :class="isSaving ? 'ph ph-spinner' : 'ph ph-floppy-disk'"></i>
            {{ isSaving ? '저장 중...' : '저장' }}
          </button>
        </div>
      </div>
    </transition>

  </div>
</template>


<script>
import { ref, computed, inject, onMounted } from "vue"

export default {
  name: "adminMember",
  setup() {
    const sql_query = inject("sql_query")

    const members = ref([])
    const selectedMember = ref(null)
    const editForm = ref({})
    const isSaving = ref(false)
    const searchText = ref("")
    const currentTab = ref("all")
    const currentPage = ref(1)
    const perPage = 20

    const levelOptions = [
      { value: 1, label: "admin" },
      { value: 0, label: "회원" },
    ]

    function levelLabel(level) {
      const found = levelOptions.find(l => l.value === level)
      return found ? found.label : `Lv.${level}`
    }

    const levelTabs = computed(() => [
      { label: "전체", value: "all", count: members.value.length },
      { label: "admin", value: 1, count: members.value.filter(m => m.level === 1).length },
      { label: "회원", value: 0, count: members.value.filter(m => m.level === 0).length },
    ])

    const filteredMembers = computed(() => {
      let list = members.value

      // 등급 필터
      if (currentTab.value !== "all") {
        if (currentTab.value === 3) {
          list = list.filter(m => m.level >= 3)
        } else {
          list = list.filter(m => m.level === currentTab.value)
        }
      }

      // 검색 필터
      if (searchText.value.trim()) {
        const q = searchText.value.trim().toLowerCase()
        list = list.filter(m =>
          (m.mb_id && m.mb_id.toLowerCase().includes(q)) ||
          (m.name && m.name.toLowerCase().includes(q)) ||
          (m.nickname && m.nickname.toLowerCase().includes(q)) ||
          (m.email && m.email.toLowerCase().includes(q))
        )
      }

      return list
    })

    const totalPages = computed(() => Math.max(1, Math.ceil(filteredMembers.value.length / perPage)))

    const pagedMembers = computed(() => {
      const start = (currentPage.value - 1) * perPage
      return filteredMembers.value.slice(start, start + perPage)
    })

    const visiblePages = computed(() => {
      const total = totalPages.value
      const cur = currentPage.value
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

      const pages = []
      pages.push(1)
      if (cur > 3) pages.push("...")
      for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
        pages.push(i)
      }
      if (cur < total - 2) pages.push("...")
      pages.push(total)
      return pages
    })

    function formatDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`
    }

    function formatDateTime(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      const date = `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`
      const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
      return `${date} ${time}`
    }

    async function fetchMembers() {
      try {
        const res = await sql_query(`SELECT * FROM member ORDER BY created_at DESC `)
        members.value = res || []
      } catch (e) {
        console.error(e)
      }
    }

    function changeTab(tab) {
      currentTab.value = tab
      currentPage.value = 1
    }

    function onSearch() {
      currentPage.value = 1
    }

    function clearSearch() {
      searchText.value = ""
      currentPage.value = 1
    }

    function selectMember(m) {
      selectedMember.value = { ...m }
      editForm.value = {
        email: m.email || '',
        password: '',
        name: m.name || '',
        first_name: m.first_name || '',
        full_name: m.full_name || '',
        nickname: m.nickname || '',
        phone: m.phone || '',
        citizen_number: m.citizen_number || '',
        address: m.address || '',
        global_address: m.global_address || '',
        introduce: m.introduce || '',
        image: m.image || '',
        group: m.group || '',
        class: m.class || '',
        license: m.license || '',
        permission: m.permission || '',
        cash: m.cash || 0,
        point: m.point || 0,
        level: m.level ?? 0,
        self_certify: m.self_certify ?? 0,
      }
    }

    async function saveMember() {
      if (!selectedMember.value || isSaving.value) return
      isSaving.value = true

      try {
        const f = editForm.value
        const esc = (v) => v == null ? '' : String(v).replace(/'/g, "''")

        let sets = [
          `email = '${esc(f.email)}'`,
          `name = '${esc(f.name)}'`,
          `first_name = '${esc(f.first_name)}'`,
          `full_name = '${esc(f.full_name)}'`,
          `nickname = '${esc(f.nickname)}'`,
          `phone = '${esc(f.phone)}'`,
          `citizen_number = '${esc(f.citizen_number)}'`,
          `address = '${esc(f.address)}'`,
          `global_address = '${esc(f.global_address)}'`,
          `introduce = '${esc(f.introduce)}'`,
          `image = '${esc(f.image)}'`,
          `\`group\` = '${esc(f.group)}'`,
          `\`class\` = '${esc(f.class)}'`,
          `license = '${esc(f.license)}'`,
          `permission = '${esc(f.permission)}'`,
          `cash = ${Number(f.cash) || 0}`,
          `point = ${Number(f.point) || 0}`,
          `level = ${Number(f.level) ?? 0}`,
          `self_certify = ${Number(f.self_certify) || 0}`,
        ]

        if (f.password && f.password.trim()) {
          sets.push(`password = '${esc(f.password)}'`)
        }

        await sql_query(`UPDATE member SET ${sets.join(', ')} WHERE idx = ${selectedMember.value.idx}`)

        // 목록 갱신
        const target = members.value.find(m => m.idx === selectedMember.value.idx)
        if (target) {
          Object.keys(f).forEach(key => {
            if (key !== 'password') target[key] = f[key]
          })
        }
        selectedMember.value = { ...selectedMember.value, ...f }
        alert('저장되었습니다.')
      } catch (e) {
        console.error(e)
        alert('저장에 실패했습니다.')
      } finally {
        isSaving.value = false
      }
    }

    async function deleteMember(member) {
      if (!confirm(`"${member.name || member.mb_id}" 회원을 삭제하시겠습니까?\n(soft delete - deleted_at 기록)`)) return
      try {
        await sql_query(`UPDATE member SET deleted_at = NOW() WHERE idx = ${member.idx}`)
        members.value = members.value.filter(m => m.idx !== member.idx)
        selectedMember.value = null
      } catch (e) {
        console.error(e)
        alert("삭제에 실패했습니다.")
      }
    }

    onMounted(() => {
      fetchMembers()
    })

    return {
      members,
      selectedMember,
      editForm,
      isSaving,
      searchText,
      currentTab,
      currentPage,
      levelOptions,
      levelTabs,
      filteredMembers,
      pagedMembers,
      totalPages,
      visiblePages,
      levelLabel,
      formatDate,
      formatDateTime,
      changeTab,
      onSearch,
      clearSearch,
      selectMember,
      saveMember,
      deleteMember,
    }
  }
}
</script>


<style scoped>
.admin-main {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: var(--color-background);
}

.admin-main > .header {
  width: 100%;
  height: 60px;
  display: flex;
  justify-content: start;
  align-items: center;
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-surface-reverse);
  padding: 16px;
  box-sizing: border-box;
}

.admin-main > .header > h2 {
  font-size: 24px;
  margin: 0;
  padding: 0;
}

.content {
  width: 100%;
  flex: 1;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
}

/* 툴바 */
.toolbar {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid var(--color-background-light);
  border-radius: 10px;
  background: var(--color-surface);
  max-width: 400px;
  transition: border-color 0.2s;
}

.search-box:focus-within {
  border-color: var(--color-brand);
}

.search-box > i {
  font-size: 16px;
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.search-box > input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--color-text-primary);
  font-size: 14px;
}

.clear-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(--color-text-secondary);
}

.filter-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tab {
  padding: 8px 16px;
  border: 1px solid var(--color-background-light);
  border-radius: 20px;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.tab:hover {
  border-color: var(--color-text-secondary);
}

.tab.active {
  background: var(--color-brand);
  color: white;
  border-color: var(--color-brand);
}

.tab > em {
  font-style: normal;
  font-size: 12px;
  background: rgba(255,255,255,0.2);
  padding: 1px 7px;
  border-radius: 10px;
}

.tab.active > em {
  background: rgba(255,255,255,0.3);
}

/* 테이블 */
.table-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--color-background-light);
  border-radius: 12px;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

thead {
  background: var(--color-surface-reverse);
}

th {
  padding: 12px 14px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

td {
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-text-primary);
  border-top: 1px solid var(--color-background-light);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

tbody > tr {
  cursor: pointer;
  transition: background 0.15s;
}

tbody > tr:hover {
  background: var(--color-surface-reverse);
}

tbody > tr.selected {
  background: var(--color-surface-reverse);
  box-shadow: inset 3px 0 0 var(--color-brand);
}

.col-id { width: 120px; }
.col-name { width: 160px; }
.col-nick { width: 110px; }
.col-email { width: 180px; }
.col-level { width: 80px; }
.col-date { width: 100px; }
.col-login { width: 100px; }

.name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
}

.avatar > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar.placeholder {
  background: var(--color-background-light);
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar.placeholder > i {
  font-size: 14px;
  color: var(--color-text-secondary);
}

/* 등급 뱃지 */
.level-badge {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.level-badge.lg {
  font-size: 13px;
  padding: 4px 14px;
}

.level-badge.lv0 {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.level-badge.lv1 {
  background: rgba(60, 130, 246, 0.15);
  color: #3c82f6;
}

.level-badge.lv2 {
  background: rgba(255, 170, 0, 0.15);
  color: #ffaa00;
}

.level-badge.lv3 {
  background: rgba(156, 163, 175, 0.15);
  color: #9ca3af;
}

/* 빈 상태 */
.empty {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  color: var(--color-text-secondary);
}

.empty > i {
  font-size: 48px;
  opacity: 0.4;
}

.empty > p {
  font-size: 14px;
  margin: 0;
}

/* 페이지네이션 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 8px 0;
}

.pagination > button {
  width: 34px;
  height: 34px;
  border: 1px solid var(--color-background-light);
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.15s;
}

.pagination > button:hover:not(:disabled) {
  border-color: var(--color-text-secondary);
  color: var(--color-text-primary);
}

.pagination > button.active {
  background: var(--color-brand);
  color: white;
  border-color: var(--color-brand);
}

.pagination > button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.dots {
  width: 34px;
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 13px;
}

/* 상세 패널 */
.detail-panel {
  position: absolute;
  right: 0;
  top: 0;
  width: 400px;
  height: 100%;
  background: var(--color-background);
  border-left: 1px solid var(--color-background-light);
  display: flex;
  flex-direction: column;
  z-index: 10;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
}

.panel-header {
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-surface-reverse);
  flex-shrink: 0;
}

.panel-header > h2 {
  font-size: 18px;
  margin: 0;
  color: var(--color-text-primary);
}

.panel-header > span {
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s;
}

.panel-header > span:hover {
  background: var(--color-surface-reverse);
}

.panel-header > span > i {
  font-size: 18px;
  color: var(--color-text-primary);
}

.panel-body {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 프로필 섹션 */
.profile-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 0;
}

.profile-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
}

.profile-avatar > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-avatar.placeholder {
  background: var(--color-background-light);
  display: flex;
  justify-content: center;
  align-items: center;
}

.profile-avatar.placeholder > i {
  font-size: 28px;
  color: var(--color-text-secondary);
}

.profile-section > h3 {
  margin: 0;
  font-size: 18px;
  color: var(--color-text-primary);
  font-weight: 600;
}

.detail-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-field > label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-field > span {
  font-size: 14px;
  color: var(--color-text-primary);
  word-break: break-all;
}

.detail-field > span.readonly {
  opacity: 0.6;
}

.detail-field > input,
.detail-field > textarea,
.detail-field .half > input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--color-background-light);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 13px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;
}

.detail-field > input:focus,
.detail-field > textarea:focus,
.detail-field .half > input:focus {
  border-color: var(--color-brand);
}

.detail-field > textarea {
  resize: vertical;
  min-height: 50px;
  font-family: inherit;
}

.detail-field.row {
  flex-direction: row;
  gap: 10px;
}

.detail-field .half {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-field .half > label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0;
}

.section-divider::before,
.section-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--color-background-light);
}

.section-divider > span {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* 등급 변경 버튼 */
.level-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.level-btn {
  padding: 6px 14px;
  border: 1px solid var(--color-background-light);
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--color-text-secondary);
}

.level-btn:hover {
  border-color: var(--color-text-secondary);
}

.level-btn.active {
  background: rgba(60, 130, 246, 0.15);
  color: #3c82f6;
  border-color: #3c82f6;
}

.level-btn.lv0.active {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border-color: #ef4444;
}

.level-btn.lv1.active {
  background: rgba(60, 130, 246, 0.15);
  color: #3c82f6;
  border-color: #3c82f6;
}

.level-btn.lv2.active {
  background: rgba(255, 170, 0, 0.15);
  color: #ffaa00;
  border-color: #ffaa00;
}

.level-btn.lv3.active {
  background: rgba(156, 163, 175, 0.15);
  color: #9ca3af;
  border-color: #9ca3af;
}

/* 패널 푸터 */
.panel-footer {
  padding: 16px;
  border-top: 1px solid var(--color-background-light);
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
}

.btn-delete {
  padding: 8px 14px;
  border: 1px solid #ef4444;
  border-radius: 8px;
  background: transparent;
  color: #ef4444;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;
}

.btn-delete:hover {
  background: rgba(239, 68, 68, 0.1);
}

.btn-save {
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  background: var(--color-brand);
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: opacity 0.2s;
}

.btn-save:hover {
  opacity: 0.9;
}

.btn-save:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 슬라이드 트랜지션 */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>